import enCA from "@/translations/en-CA.json"
import frCA from "@/translations/fr-CA.json"

/**
 * The ATS Hub's user-facing strings. Keys are flat, dotted and namespaced by screen
 * ("explorer.toolbar.pull"); the English file defines the key set and the French file
 * must carry every key (checked by a test). Values may hold {{placeholders}}.
 */

export const LOCALES = ["en-CA", "fr-CA"] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = "en-CA"

export type TranslationKey = keyof typeof enCA
export type Translations = Record<TranslationKey, string>

export const translations: Record<Locale, Translations> = {
  "en-CA": enCA,
  "fr-CA": frCA as Translations,
}

export type TranslationVariables = Record<string, string | number | null | undefined>

/** The host passes the user's culture; anything that is not French reads as English */
export function toLocale(culture: string | null | undefined): Locale {
  return culture?.toLowerCase().startsWith("fr") ? "fr-CA" : DEFAULT_LOCALE
}

export function isLocale(value: string | null | undefined): value is Locale {
  return LOCALES.includes(value as Locale)
}

/** Replaces {{name}} placeholders; an unknown placeholder is left in place so it shows up in review */
export function interpolate(template: string, variables?: TranslationVariables): string {
  if (!variables) return template
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = variables[name]
    return value === null || value === undefined ? match : String(value)
  })
}

/** The string for a key in a locale, falling back to English, then to the key itself */
export function translate(locale: Locale, key: TranslationKey, variables?: TranslationVariables): string {
  const template = translations[locale][key] ?? translations[DEFAULT_LOCALE][key] ?? key
  return interpolate(template, variables)
}

/** Formats a count with the locale's digit grouping */
export function formatNumber(locale: Locale, value: number): string {
  return new Intl.NumberFormat(locale).format(value)
}
